/**
 * 2D affine matrix helpers for INSERT rendering.
 *
 * Extracted from blockRender.tsx to keep the main renderer file under
 * the 500-line ceiling (CLAUDE.md). These helpers are pure (no JSX,
 * no React) and reusable by other consumers (e.g. EXPLODE mutation
 * which needs to transform block child entities by the INSERT matrix
 * to produce world-space entities).
 */

/**
 * 2D affine matrix stored in SVG column-major form:
 *
 *     [ a  c  e ]
 *     [ b  d  f ]
 *     [ 0  0  1 ]
 *
 * Which yields the SVG `matrix(a,b,c,d,e,f)` transform where a point
 * (x, y) maps to `(a*x + c*y + e, b*x + d*y + f)`.
 */
export interface BlockMatrix {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
  /** Serialize as `matrix(a b c d e f)` for use in an SVG `transform`. */
  toSvgTransform(): string;
  /** Return `this × other` (applies `other` first, then `this`). */
  multiply(other: BlockMatrix): BlockMatrix;
  /**
   * Post-multiply by a translation matrix: useful for MINSERT grid
   * stepping (the offset is applied in the block's local coordinate
   * system, before the outer transform).
   */
  translate(offset: { x: number; y: number }): BlockMatrix;
  /** Apply the transform to a point. */
  transformPoint(p: { x: number; y: number }): { x: number; y: number };
}

function mat(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number,
): BlockMatrix {
  return {
    a,
    b,
    c,
    d,
    e,
    f,
    toSvgTransform() {
      return `matrix(${a} ${b} ${c} ${d} ${e} ${f})`;
    },
    multiply(other: BlockMatrix): BlockMatrix {
      return mat(
        a * other.a + c * other.b,
        b * other.a + d * other.b,
        a * other.c + c * other.d,
        b * other.c + d * other.d,
        a * other.e + c * other.f + e,
        b * other.e + d * other.f + f,
      );
    },
    translate(offset: { x: number; y: number }): BlockMatrix {
      return mat(
        a,
        b,
        c,
        d,
        a * offset.x + c * offset.y + e,
        b * offset.x + d * offset.y + f,
      );
    },
    transformPoint(p: { x: number; y: number }) {
      return {
        x: a * p.x + c * p.y + e,
        y: b * p.x + d * p.y + f,
      };
    },
  };
}

/**
 * Build the translate/rotate/scale sub-matrices {@link composeBlockMatrix}
 * composes. `rotateDeg` is negated (SVG-authoring convention — see
 * `composeBlockMatrix`'s own doc below) since the raw SVG `rotate()` argument
 * is built from a CAD-space CCW rotation value.
 */
function buildTrsMats(input: {
  translate: { x: number; y: number };
  rotateDeg: number;
  scaleX: number;
  scaleY: number;
}): { tMat: BlockMatrix; rMat: BlockMatrix; sMat: BlockMatrix } {
  const { translate, rotateDeg, scaleX, scaleY } = input;
  const rad = (-rotateDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    tMat: mat(1, 0, 0, 1, translate.x, translate.y),
    rMat: mat(cos, sin, -sin, cos, 0, 0),
    sMat: mat(scaleX, 0, 0, scaleY, 0, 0),
  };
}

/**
 * Compose an INSERT's transform matrix:
 *
 *     T(insertion) × R(rotation) × S(scaleX, scaleY) × T(-basePoint)
 *
 * The block's entities are first translated so the basePoint lands at
 * the local origin (AutoCAD convention: INSERT places the block's base
 * point onto the insertion point), then scaled — negative values
 * mirror — then rotated, then shifted to the insertion location.
 *
 * Rotation is given in degrees measured CCW in CAD space (the
 * PrimitiveAtlasStage parent applies `scale(1,-1)` in CAD mode, so the
 * matrix here is authored in SVG sign convention and inverted via the
 * `-rotateDeg` on entry — the result reads naturally when composed
 * inside the CAD-space `<g>`).
 */
export function composeBlockMatrix(input: {
  translate: { x: number; y: number };
  rotateDeg: number;
  scaleX: number;
  scaleY: number;
  basePoint?: { x: number; y: number };
}): BlockMatrix {
  const { tMat, rMat, sMat } = buildTrsMats(input);
  const bx = input.basePoint?.x ?? 0;
  const by = input.basePoint?.y ?? 0;
  const bMat = mat(1, 0, 0, 1, -bx, -by);

  // Compose right-to-left: first translate by -basePoint, then scale,
  // then rotate, then translate to insertion.
  return tMat.multiply(rMat).multiply(sMat).multiply(bMat);
}
